import { useMemo, useRef, useState, type FC, type ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import InputBase from '@mui/material/InputBase'
import type { GridEditorProps } from '../types'
import classes from '../grid-theme.module.scss'

export interface SelectEditorParams {
  /** The options offered. Resolve per row by making `editorParams` a function. */
  values?: string[]
  /** Free text is rejected, as it was in the rich-select editor. */
  placeholder?: string
  /** Renders an option's row; the plain label is used when absent. */
  renderOption?: (option: string) => ReactNode
  /**
   * Widens the list beyond the cell it drops out of. Without it the list is
   * exactly as wide as the column, which is too narrow for a multi-line option.
   * The list still caps at the viewport (see `.selectEditorPaper`).
   */
  minWidth?: number
  /**
   * Sorts the options into named sections. A long flat list of terms from the
   * same domain is hard to scan — the eye has nothing to navigate by. Options
   * must already be ordered by group; MUI starts a new heading each time this
   * returns something different, so an unsorted list produces the same heading
   * several times over.
   */
  groupBy?: (option: string) => string
}

const asOptions = (params?: Record<string, unknown>): string[] => {
  const values = params?.values
  return Array.isArray(values) ? values.map((value) => String(value)) : []
}

/**
 * Searchable dropdown cell editor — the replacement for `agRichSelectCellEditor`
 * with `allowTyping` + `filterList` + `highlightMatch`, which is how every
 * dropdown column in this app was configured.
 *
 * The list opens when the user asks for it: a click on the cell's caret, typing
 * to filter, or Alt+Down. It used to open with the editor, which meant every
 * click and every arrow key into the column dropped a list over the rows below
 * — covering the row the user was working towards.
 *
 * Typing filters, arrows move within the list, Enter takes the highlighted
 * option. Escape closes the list first and the editor second. Clicking away
 * leaves the cell as it was: an editor that commits on blur would let a stray
 * click write a half-typed value, and unlike a text cell there is nothing
 * meaningful to keep from a partial match.
 */
const SelectEditor: FC<GridEditorProps<unknown>> = ({
  value,
  params,
  openOnMount = false,
  onCommit,
  onCancel,
}) => {
  const options = useMemo(() => asOptions(params), [params])
  const current = value == null ? '' : String(value)
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(openOnMount)
  // A commit and the blur that follows it arrive together; without this the
  // blur handler would cancel the edit that just succeeded.
  const committedRef = useRef(false)

  const renderOption = params?.renderOption as
    | ((option: string) => ReactNode)
    | undefined
  const groupBy = params?.groupBy as ((option: string) => string) | undefined
  const minWidth =
    typeof params?.minWidth === 'number' ? params.minWidth : undefined

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      autoHighlight
      // Keeps the caret visible while the list is open, so the control the user
      // clicked to open it is the one that closes it.
      forcePopupIcon
      disablePortal={false}
      disableClearable
      size="small"
      options={options}
      groupBy={groupBy}
      value={options.includes(current) ? current : undefined}
      inputValue={input}
      onInputChange={(_event, next, reason) => {
        if (reason !== 'reset') setInput(next)
      }}
      onChange={(_event, next) => {
        committedRef.current = true
        onCommit(next ?? current)
      }}
      // Picking the option that is already selected is a decision too — the
      // user has confirmed the value and expects the list to go away. MUI does
      // not fire `onChange` for it (its `handleValue` returns early when the
      // value has not moved), so the close is what tells us it happened. There
      // is nothing to write, so this closes the editor rather than committing.
      onClose={(_event, reason) => {
        setOpen(false)
        if (reason !== 'selectOption' || committedRef.current) return
        committedRef.current = true
        onCancel()
      }}
      // The cell is only ~40px tall, so the list has to escape it.
      slotProps={{
        paper: {
          className: classes.selectEditorPaper,
          style: minWidth ? { minWidth } : undefined,
        },
        popper: {
          // Autocomplete pins the list to the anchor's width; `auto` hands
          // sizing back to the paper so `minWidth` can take effect.
          style: { zIndex: 1400, ...(minWidth ? { width: 'auto' } : {}) },
          placement: 'bottom-start',
        },
      }}
      ListboxProps={{ className: classes.selectEditorList }}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option}>
          {/* The tick keeps its space whether or not it is drawn, so the
              labels stay in one column instead of stepping sideways as the
              selection moves. */}
          <Box component="span" className={classes.selectOptionCheck} aria-hidden>
            <svg width="14" height="14" viewBox="0 0 16 16">
              <path
                d="M3.5 8.5l3 3 6-6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          <Box
            component="span"
            className={classes.selectOptionLabel}
            // A configuration name can outrun any sensible list width; the
            // full text is one hover away rather than forcing the list wider
            // than the grid underneath it.
            title={typeof option === 'string' ? option : undefined}
          >
            {renderOption ? renderOption(option) : option}
          </Box>
        </Box>
      )}
      renderInput={(inputParams) => (
        <InputBase
          {...inputParams.InputProps}
          inputProps={inputParams.inputProps}
          autoFocus
          className={classes.editorInput}
          placeholder={
            (params?.placeholder as string | undefined) ?? (current || undefined)
          }
          onBlur={() => {
            if (committedRef.current) return
            onCancel()
          }}
        />
      )}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // With the list open Escape belongs to the list; MUI closes it and
          // the cell stays as it was. Only a second Escape leaves the cell.
          if (open) return
          event.preventDefault()
          event.stopPropagation()
          committedRef.current = true
          onCancel()
          return
        }
        // The keyboard way in, for a column being walked with the arrows.
        if (!open && event.altKey && event.key === 'ArrowDown') {
          event.preventDefault()
          event.stopPropagation()
          setOpen(true)
        }
      }}
      className={classes.selectEditor}
    />
  )
}

export default SelectEditor
